import React from 'react';
import { Icon } from '@iconify/react';

// Markdown Insert Handler
export const handleMarkdownInsert = (
  action: string,
  textarea: HTMLTextAreaElement | null,
  getValue: () => string,
  setValue: (value: string) => void
) => {
  if (!textarea) return;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = getValue();
  let newValue = value;
  let newStart = start;
  let newEnd = end;
  const selected = value.slice(start, end);
  const before = value.slice(0, start);
  const after = value.slice(end);

  const getSelectedLines = () => {
    let selStart = start;
    while (selStart > 0 && value[selStart - 1] !== '\n') {
      selStart--;
    }

    let selEnd = end;
    while (selEnd < value.length && value[selEnd] !== '\n') {
      selEnd++;
    }

    return {
      lines: value.slice(selStart, selEnd).split('\n'),
      selStart,
      selEnd
    };
  };

  const handleWrapFormatting = (wrap: string) => {
    const beforeWrap = value.slice(start - wrap.length, start);
    const afterWrap = value.slice(end, end + wrap.length);

    if (beforeWrap === wrap && afterWrap === wrap) {
      newValue = value.slice(0, start - wrap.length) + selected + value.slice(end + wrap.length);
      newStart = start - wrap.length;
      newEnd = end - wrap.length;
    } else {
      newValue = before + wrap + selected + wrap + after;
      newStart = start + wrap.length;
      newEnd = end + wrap.length;
    }
  };

  const handleLineFormatting = (prefix: string, cleanLineFn: (line: string) => string) => {
    const { lines, selStart, selEnd } = getSelectedLines();
    const newLines = lines.map(cleanLineFn);
    newValue = value.slice(0, selStart) + newLines.join('\n') + value.slice(selEnd);
    newStart = newEnd = selStart + prefix.length;
  };

  switch (action) {
    case 'bold':
      handleWrapFormatting('**');
      break;
    case 'italic':
      handleWrapFormatting('*');
      break;
    case 'heading1':
      handleLineFormatting('# ', (line: string) => {
        const cleanLine = line.startsWith('# ') ? line.substring(2) : line;
        return '# ' + cleanLine;
      });
      break;
    case 'heading2':
      handleLineFormatting('## ', (line: string) => {
        const cleanLine = line.startsWith('## ') ? line.substring(3) : line;
        return '## ' + cleanLine;
      });
      break;
    case 'blockquote':
      handleLineFormatting('> ', (line: string) => {
        const cleanLine = line.startsWith('> ') ? line.substring(2) : line;
        return '> ' + cleanLine;
      });
      break;
    case 'bulleted':
      handleLineFormatting('- ', (line: string) => {
        const cleanLine = line.startsWith('- ') ? line.substring(2) : line;
        return '- ' + cleanLine;
      });
      break;
    case 'numbered': {
      const { lines, selStart, selEnd } = getSelectedLines();
      const newLines = lines.map((line: string, i: number) => {
        const cleanLine = line.replace(/^\d+\.\s/, '');
        return `${i + 1}. ${cleanLine}`;
      });
      newValue = value.slice(0, selStart) + newLines.join('\n') + value.slice(selEnd);
      newStart = newEnd = selStart + 3;
      break;
    }
    case 'link':
      if (selected) {
        newValue = before + `[${selected}](https://example.com)` + after;
        newStart = start + 1;
        newEnd = start + 1 + selected.length;
      } else {
        newValue = before + `[link text](https://example.com)` + after;
        newStart = newEnd = start + 1;
      }
      break;
  }

  setValue(newValue);

  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(newStart, newEnd);
  }, 0);
};

// Markdown Toolbar Component
interface MarkdownToolbarProps {
  onInsert: (action: string) => void;
}

const toolbarButtons = [
  { label: 'Bold', icon: 'mdi:format-bold', action: 'bold' },
  { label: 'Italic', icon: 'mdi:format-italic', action: 'italic' },
  { label: 'Heading 1', icon: 'mdi:format-header-1', action: 'heading1' },
  { label: 'Heading 2', icon: 'mdi:format-header-2', action: 'heading2' },
  { label: 'Blockquote', icon: 'mdi:format-quote-close', action: 'blockquote' },
  { label: 'Bulleted List', icon: 'mdi:format-list-bulleted', action: 'bulleted' },
  { label: 'Numbered List', icon: 'mdi:format-list-numbered', action: 'numbered' },
  { label: 'Link', icon: 'mdi:link-variant', action: 'link' },
];

// Function to process markdown text and convert literal \n strings to actual newlines
export const processMarkdownText = (text: string): string => {
  if (!text) return '';
  return text.replace(/\\n/g, '\n');
};

export const MarkdownToolbar: React.FC<MarkdownToolbarProps> = ({ onInsert }) => (
  <div className="flex items-center gap-2 border border-default-200 rounded-md p-2 bg-content2 mb-2 w-fit">
    {toolbarButtons.map(({ label, icon, action }) => (
      <button
        key={label}
        type="button"
        title={label}
        className="p-1 rounded hover:bg-default-200 transition-colors"
        onClick={() => onInsert(action)}
      >
        <Icon icon={icon} width="1.2em" height="1.2em" className="text-default-500" />
      </button>
    ))}
  </div>
);

// Markdown Preview Styles
export const markdownPreviewStyles = `
  .custom-resize textarea {
    resize: vertical;
    min-height: 100px;
    max-height: 600px;
  }
  .markdown-preview {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafbfc;
    padding: 16px;
    min-height: 100px;
    max-height: 600px;
    overflow: auto;
    font-family: inherit;
    color: #222;
  }
  .markdown-preview h1 {
    font-size: 1.2rem;
    font-weight: bold;
    margin: 0.5em 0 0.25em 0;
  }
  .markdown-preview h2 {
    font-size: 1.1rem;
    font-weight: bold;
    margin: 0.5em 0 0.25em 0;
  }
  .markdown-preview h3 {
    font-size: 1.05rem;
    font-weight: bold;
    margin: 0.5em 0 0.25em 0;
  }
  .markdown-preview ul {
    list-style-type: disc;
    margin-left: 1.5em;
    font-size: 1em;
  }
  .markdown-preview ul ul {
    list-style-type: disc;
    margin-left: 1.5em;
    font-size: 1em;
  }
  .markdown-preview ol {
    list-style-type: decimal;
    margin-left: 1.5em;
    font-size: 1em;
  }
  .markdown-preview ol ol {
    list-style-type: decimal;
    margin-left: 1.5em;
    font-size: 1em;
  }
  .markdown-preview blockquote {
    border-left: 4px solid #ddd;
    margin: 0;
    padding-left: 1em;
    color: #666;
    font-style: italic;
  }
  .markdown-preview blockquote p {
    margin: 0.5em 0;
  }
  .dark .markdown-preview {
    border-color: var(--cty-border);
    background: var(--cty-bg-surface);
    color: var(--cty-text-primary);
  }
  .dark .markdown-preview blockquote {
    border-left-color: var(--cty-border);
    color: var(--cty-text-secondary);
  }
`; 